import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, effect, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlayoffStateService } from '../../services/playoff/playoff-state.service';
import { Tournament } from '../../models';
import { PlayoffHoleDetail, PlayoffPlayerDetail, PlayoffScoreDetail } from '../../models/playoff-detail';
import { DialogService } from '../../services/shared/dialog/dialog.service';
import { PlayoffManagementComponent } from './playoff-management.component';
import { PlayoffService } from '../../services/playoff/playoff.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-playoff-workspace',
  standalone: true,
  imports: [CommonModule, PlayoffManagementComponent],
  templateUrl: './playoff-workspace.component.html',
  styleUrls: ['./playoff-workspace.component.scss']
})
export class PlayoffWorkspaceComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly playoffState = inject(PlayoffStateService);
  private readonly dialogService = inject(DialogService);
  private readonly playoffService = inject(PlayoffService);
  private readonly toastr = inject(ToastrService);
  protected currentTournament: Tournament | null = null;

  readonly isLoading = computed(() => this.playoffState.isLoading());
  readonly detail = computed(() => this.playoffState.detail());
  readonly holes = computed(() => this.detail()?.holes ?? []);
  readonly players = computed(() => this.detail()?.players ?? []);
  readonly error = computed(() => this.playoffState.error());

  private readonly creationPromptEffect = effect(() => {
    if (!this.playoffState.needsCreation()) {
      return;
    }

    const tournament = this.currentTournament;
    if (!tournament) {
      this.playoffState.clearCreationRequest();
      return;
    }

    void Promise.resolve().then(() => this.handleMissingPlayoff(tournament));
  });

  ngOnInit(): void {
    const parentRoute = this.route.parent;
    if (parentRoute) {
      parentRoute.data
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((data) => {
          const tournament = data['tournament'] as Tournament | undefined;
          this.currentTournament = tournament ?? null;
          this.playoffState.initializeForTournament(tournament);
        });
    } else {
      this.playoffState.reset();
    }
  }

  private async handleMissingPlayoff(tournament: Tournament): Promise<void> {
    this.playoffState.clearCreationRequest();

    const confirmed = await this.dialogService.confirm(
      'Playoff does not exist. Set up a Playoff now?\nThis will set up playoff between the players currently tied for first place. Continue?',
      {
        title: 'Set up Playoff',
        confirmLabel: 'Yes',
        cancelLabel: 'No'
      }
    );

    if (confirmed) {
      const created = await this.playoffState.createPlayoff();
      if (!created) {
        this.playoffState.requestCreationPrompt();
      }
      return;
    }

    this.playoffState.reset();
    await this.router.navigate(['../'], { relativeTo: this.route });
  }

  trackHole(_: number, hole: PlayoffHoleDetail): string {
    return hole.playoffHoleId;
  }

  trackPlayer(_: number, player: PlayoffPlayerDetail): string {
    return player.playoffPlayerId;
  }

  scoreForHole(player: PlayoffPlayerDetail, hole: PlayoffHoleDetail): string {
    const score = player.scores.find((entry) => entry.playoffHoleId === hole.playoffHoleId);
    return score?.score !== undefined && score?.score !== null ? String(score.score) : '—';
  }

  scoreClass(player: PlayoffPlayerDetail, hole: PlayoffHoleDetail): Record<string, boolean> {
    const score = player.scores.find((entry) => entry.playoffHoleId === hole.playoffHoleId);
    return {
      'score-missing': !score || score.score === undefined || score.score === null,
      'score-verified': !!score?.verified
    };
  }

  totalForPlayer(player: PlayoffPlayerDetail): string {
    const totals = player.scores.filter((score) => score.score !== undefined && score.score !== null);
    if (totals.length === 0) {
      return '—';
    }

    const sum = totals.reduce((acc, score) => acc + (score.score ?? 0), 0);
    return String(sum);
  }

  statusLabel(status: string): string {
    switch ((status ?? '').toUpperCase()) {
      case 'WNR':
        return 'Winner';
      case 'ELM':
        return 'Eliminated';
      case 'ACT':
        return 'Active';
      default:
        return status || 'Unknown';
    }
  }

  statusClass(status: string): Record<string, boolean> {
    const normalized = (status ?? '').toUpperCase();
    return {
      'label-success': normalized === 'WNR',
      'label-danger': normalized === 'ELM',
      'label-primary': normalized === 'ACT',
      'label-default': normalized !== 'WNR' && normalized !== 'ELM' && normalized !== 'ACT'
    };
  }

  isScoreVerified(player: PlayoffPlayerDetail, hole: PlayoffHoleDetail): boolean {
    const score = player.scores.find((entry) => entry.playoffHoleId === hole.playoffHoleId);
    return !!score?.verified;
  }

  async onScoreChange(
    player: PlayoffPlayerDetail,
    hole: PlayoffHoleDetail,
    event: Event
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const newScore = input.value.trim();

    // If empty, we could delete the score or just ignore
    if (newScore === '') {
      return;
    }

    const scoreValue = parseInt(newScore, 10);
    if (isNaN(scoreValue) || scoreValue < 1 || scoreValue > 20) {
      this.toastr.warning('Please enter a valid score between 1 and 20');
      input.value = this.scoreForHole(player, hole) === '—' ? '' : this.scoreForHole(player, hole);
      return;
    }

    const existingScore = player.scores.find((s) => s.playoffHoleId === hole.playoffHoleId);
    const detail = this.detail();
    if (!detail) {
      return;
    }

    if (!player.playoffScorecardId) {
      this.toastr.error('No scorecard found for player');
      return;
    }

    try {
      if (existingScore && existingScore.playoffScoreId) {
        // Update existing score
        await this.playoffService.updatePlayoffScore(
          detail.playoffId,
          player.playoffPlayerId,
          player.playoffScorecardId,
          existingScore.playoffScoreId,
          {
            playoffHoleId: hole.playoffHoleId,
            score: scoreValue,
            submittedAt: new Date().toISOString(),
            submittedBy: 'Admin',
            verified: true // Mark as verified when saved through Admin
          }
        ).toPromise();

        // Update local state
        existingScore.score = scoreValue;
        existingScore.verified = true;
        this.toastr.success('Score updated successfully');
      } else {
        // Create new score
        const newScoreId = await this.playoffService.createPlayoffScore(
          detail.playoffId,
          player.playoffPlayerId,
          player.playoffScorecardId,
          {
            playoffHoleId: hole.playoffHoleId,
            score: scoreValue,
            submittedAt: new Date().toISOString(),
            submittedBy: 'Admin',
            verified: true
          }
        ).toPromise();

        // Add to local state
        const newScoreEntry: PlayoffScoreDetail = {
          playoffScoreId: newScoreId,
          playoffHoleId: hole.playoffHoleId,
          playoffHoleLegacyId: 0,
          score: scoreValue,
          submittedAt: new Date().toISOString(),
          submittedBy: 'Admin',
          verified: true
        };
        player.scores.push(newScoreEntry);
        this.toastr.success('Score added successfully');
      }
    } catch (error) {
      console.error('Error saving score:', error);
      this.toastr.error('Failed to save score');
      // Revert the input value
      input.value = existingScore?.score?.toString() ?? '';
    }
  }

  async onStatusChange(player: PlayoffPlayerDetail, event: Event): Promise<void> {
    const select = event.target as HTMLSelectElement;
    const newStatus = select.value;

    const detail = this.detail();
    if (!detail) {
      return;
    }

    try {
      // If setting to WNR, automatically set all other players to ELM
      if (newStatus === 'WNR') {
        const confirmed = await this.dialogService.confirm(
          `Mark ${player.firstName} ${player.lastName} as the winner? All other players will be eliminated.`,
          {
            title: 'Confirm Winner',
            confirmLabel: 'Yes, Set Winner',
            cancelLabel: 'Cancel'
          }
        );

        if (!confirmed) {
          select.value = player.status; // Revert
          return;
        }

        // Update all players
        const updatePromises = this.players().map(async (p) => {
          const targetStatus = p.playoffPlayerId === player.playoffPlayerId ? 'WNR' : 'ELM';
          await this.playoffService.updatePlayoffPlayer(
            detail.playoffId,
            p.playoffPlayerId,
            {
              lpgaId: p.lpgaId,
              wwgrId: p.wwgrId,
              firstName: p.firstName,
              lastName: p.lastName,
              country: p.country,
              isAmateur: p.isAmateur,
              status: targetStatus
            }
          ).toPromise();

          // Update local state
          p.status = targetStatus;
        });

        await Promise.all(updatePromises);
        this.toastr.success('Winner set and other players eliminated');
      } else {
        // Regular status update
        await this.playoffService.updatePlayoffPlayer(
          detail.playoffId,
          player.playoffPlayerId,
          {
            lpgaId: player.lpgaId,
            wwgrId: player.wwgrId,
            firstName: player.firstName,
            lastName: player.lastName,
            country: player.country,
            isAmateur: player.isAmateur,
            status: newStatus
          }
        ).toPromise();

        // Update local state
        player.status = newStatus;
        this.toastr.success('Player status updated');
      }
    } catch (error) {
      console.error('Error updating player status:', error);
      this.toastr.error('Failed to update player status');
      // Revert the select value
      select.value = player.status;
    }
  }
}
